// src/pages/SecurityPermission/SecurityPermissionPage.jsx

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  getPagedPermissions,
  deletePermission,
  updatePermission,
} from "../../services/securityPermissionService";
import { formatDateTime } from "../../utils/formatters";
import PaginatedTable from "../Common/PaginatedTable";
import EntityDetailsSidebar from "../Common/EntityDetailsSidebar";
import {
  Pencil,
  Trash2,
  Eye,
  ShieldCheck,
  CheckCircle,
  XCircle,
} from "lucide-react";

const SecurityPermissionPage = () => {
  const navigate = useNavigate();

  // Estados
  const [permissions, setPermissions] = useState([]);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // Esquema para el detalle lateral
  const schema = [
    { label: "Clave", key: "key" },
    { label: "Descripción", key: "description" },
    { label: "Módulo", key: "module" },
    { label: "Acción", key: "action" },
    {
      label: "Creado",
      key: "createdAt",
      render: (value) => formatDateTime(value),
    },
    {
      label: "Actualizado",
      key: "updatedAt",
      render: (value) => formatDateTime(value),
    },
  ];

  // Obtener permisos con paginación y búsqueda
  const fetchPermissions = useCallback(async (page, size, search = "") => {
    try {
      const response = await getPagedPermissions(page, size, search);
      if (response.success) {
        setPermissions(response.data.items);
        setTotalRows(response.data.totalCount);
      }
    } catch {
      Swal.fire("Error", "No se pudieron cargar los permisos", "error");
    }
  }, []);

  useEffect(() => {
    fetchPermissions(currentPage, perPage, searchTerm);
  }, [fetchPermissions, currentPage, perPage, searchTerm]);

  // Paginación
  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  // Búsqueda
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // Acciones
  const handleEdit = (id) => {
    navigate(`/dashboard/security-permission/edit/${id}`);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará el permiso.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await deletePermission(id);
        if (response.success) {
          Swal.fire("Eliminado", "El permiso ha sido eliminado.", "success");
          fetchPermissions(currentPage, perPage, searchTerm);
        } else {
          throw new Error(response.message);
        }
      } catch {
        Swal.fire("Error", "No se pudo eliminar el permiso", "error");
      }
    }
  };

  const handleViewDetails = (perm) => setSelectedPermission(perm);

  // Columnas para la tabla
  const columns = [
    {
      name: "Módulo",
      selector: (row) => row.module,
      sortable: true,
      wrap: true,
    },
    {
      name: "Clave",
      selector: (row) => row.key,
      sortable: true,
      wrap: true,
    },
    {
      name: "Descripción",
      selector: (row) => row.description,
      wrap: true,
    },
    {
      name: "Activo",
      center: true,
      cell: (row) => (
        <Button
          variant="light"
          size="sm"
          className="d-flex align-items-center gap-1 border-0"
          title={row.isActive ? "Desactivar permiso" : "Activar permiso"}
          onClick={async (e) => {
            e.stopPropagation();
            const result = await Swal.fire({
              title: row.isActive
                ? "¿Deseas inactivar este permiso?"
                : "¿Deseas activar este permiso?",
              icon: "question",
              showCancelButton: true,
              confirmButtonText: "Sí, confirmar",
              cancelButtonText: "Cancelar",
            });

            if (result.isConfirmed) {
              try {
                const updated = {
                  ...row,
                  isActive: !row.isActive,
                };

                // Asume que ya tienes un endpoint tipo PUT
                const response = await updatePermission(updated);

                if (response.success) {
                  Swal.fire(
                    "Éxito",
                    "Estado actualizado correctamente",
                    "success"
                  );
                  fetchPermissions(currentPage, perPage, searchTerm);
                } else {
                  throw new Error(response.message);
                }
              } catch {
                Swal.fire("Error", "No se pudo actualizar el estado", "error");
              }
            }
          }}
        >
          {row.isActive ? (
            <>
              <CheckCircle size={16} className="text-success" /> Activo
            </>
          ) : (
            <>
              <XCircle size={16} className="text-danger" /> Inactivo
            </>
          )}
        </Button>
      ),
      wrap: true,
      width: "150px",
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row.id);
            }}
            title="Editar"
          >
            <Pencil size={16} />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row.id);
            }}
            title="Eliminar"
          >
            <Trash2 size={16} />
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(row);
            }}
            title="Ver detalles"
          >
            <Eye size={16} />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      wrap: true,
      width: "150px",
      center: true,
    },
  ];

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0 text-dark">Permisos del sistema</h4>
          <Button
            onClick={() => navigate("/dashboard/security-permission/create")}
          >
            + Crear nuevo permiso
          </Button>
        </div>

        <PaginatedTable
          keyField="id"
          columns={columns}
          data={permissions}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePerRowsChange}
          onSearch={handleSearch}
        />
      </Card>

      <EntityDetailsSidebar
        isVisible={!!selectedPermission}
        onClose={() => setSelectedPermission(null)}
        title="Detalles del permiso"
        icon={<ShieldCheck size={20} />}
        data={selectedPermission}
        schema={schema}
      />
    </div>
  );
};

export default SecurityPermissionPage;
